import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DataTableSort } from "@/components/shared/data-table/DataTable";
import { fetchAllFilteredRows } from "@/components/shared/data-table/data-table-export";
import { useInlineEditMutation } from "@/components/shared/data-table/use-inline-edit-mutation";
import { reconcileProductWrite } from "@/features/finance/hooks/reconcile-product-write";
import { productKeys } from "@/features/inventory/hooks/query-keys";
import { DEFAULT_INVENTORY_SORT } from "@/features/inventory/pages/inventory-search-params";
import type {
  InventoryScopeId,
  Product,
  ProductWithStock,
  UpdateProductPayload,
} from "@/features/inventory/types";
import { getBackendClient, type QueryBuilder } from "@/lib/backend";
import { orderListPage } from "@/lib/db/list-order";
import { fetchListTotals } from "@/lib/db/list-totals";
import { updateProductRecord } from "@/lib/db/products";
import { PRODUCT_SELECT } from "@/lib/db/selects";
import { toPostgrestIlikeContains } from "@/lib/utils/query-helpers";

const dataClient = getBackendClient().data;

const PRODUCT_STOCK_VIEW = "product_stock";

/**
 * List projection for the inventory table.
 *
 * `product_stock` re-exposes every `gl_products` column, so this is the
 * `PRODUCT_SELECT` set the list actually renders plus the derived stock
 * position. `kanban_status` is deliberately absent: all rows carry the same
 * value, the app no longer reads it, and stock comes from the view instead.
 */
const PRODUCT_STOCK_SELECT = `
  id, product_name, vendor_product_name, sku, uid,
  quantity, cost, total_cost, category, photo_url,
  is_sample, is_fronted, is_miscellaneous, is_service, is_deleted,
  purchase_order_id, purchase_order_uid, account_id, created_at, notes,
  product_id, qty_sold, qty_sampled, qty_in_stock, qty_oversold, stock_scope
`;

/**
 * Detail projection for one product.
 *
 * The same fields the list reads, plus `kanban_status`, which the quick view
 * still renders. Read from `product_stock` rather than `gl_products` so a
 * detail surface can answer how many units are actually on the shelf: the list
 * is scoped by that position, and a lot's recorded `quantity` says only how
 * many arrived.
 */
const PRODUCT_STOCK_DETAIL_SELECT = `
  id, product_name, vendor_product_name, sku, uid,
  quantity, cost, total_cost, category, kanban_status, photo_url,
  is_sample, is_fronted, is_miscellaneous, is_service, is_deleted,
  purchase_order_id, purchase_order_uid, account_id, created_at, notes,
  product_id, qty_sold, qty_sampled, qty_in_stock, qty_oversold, stock_scope
`;

interface InventoryProductsParams {
  limit: number;
  offset: number;
  scope: InventoryScopeId;
  search?: string;
  sort?: DataTableSort | null;
}

/**
 * `product_stock` is a view and the generated `Database` type carries tables
 * only, so `DataClient.from` cannot name it. The cast is confined to this one
 * function rather than loosening the client for every caller.
 */
function fromProductStock(): QueryBuilder<ProductWithStock> {
  return (dataClient.from as unknown as (table: string) => QueryBuilder<ProductWithStock>)(
    PRODUCT_STOCK_VIEW,
  );
}

async function getInventoryProducts(params: InventoryProductsParams) {
  const limit = params.limit;
  const offset = Math.max(0, params.offset);
  const sort = params.sort ?? DEFAULT_INVENTORY_SORT;

  let query = fromProductStock().select(PRODUCT_STOCK_SELECT).eq("is_deleted", false);

  if (params.scope !== "all") {
    query = query.eq("stock_scope", params.scope);
  }

  if (params.search?.trim()) {
    // `search_text` is a computed column on product_stock (see
    // migrations/20260726160000_add-list-search-and-totals.sql): product and
    // vendor product name, sku, uid, category, purchase-order uid, notes and
    // cost, in one predicate. The totals RPC filters with the same function,
    // so the strip under the table counts the products the table is listing.
    const search = toPostgrestIlikeContains(params.search.trim());
    query = query.or(`search_text.ilike.${search}`);
  }

  // 1,343 live products share three `created_at` values, so the default sort
  // ties across both 500-row export page boundaries. Without the unique key
  // `orderListPage` appends, that walk returned 145 products twice and dropped
  // 145 entirely.
  const { data, error } = await orderListPage(query, {
    ascending: sort.direction === "asc",
    column: sort.field,
  }).range(offset, offset + limit - 1);

  if (error) {
    throw new Error(error.message);
  }

  return (data || []) as ProductWithStock[];
}

/**
 * One product with its derived stock position, or `null` when no live row
 * carries that id.
 *
 * A missing product is an ordinary outcome here — the list soft-deletes
 * products, and a link or a peek can outlive the row it names. Every surface
 * reading this query already renders a not-found state for absent data, so
 * absence stays absence instead of arriving as a failure the operator is
 * invited to retry.
 */
async function getInventoryProduct(id: string) {
  const { data, error } = await fromProductStock()
    .select(PRODUCT_STOCK_DETAIL_SELECT)
    .eq("id", id)
    .eq("is_deleted", false)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  return data ?? null;
}

export function updateInventoryProductRecord(id: string, payload: UpdateProductPayload) {
  return updateProductRecord<Product>(id, payload);
}

export async function deleteInventoryProductRecord(id: string): Promise<Product> {
  const updates: Record<string, unknown> = {
    last_modified_by_source: "database",
    is_deleted: true,
    deleted_at: new Date().toISOString(),
  };

  const { data, error } = await dataClient
    .from("gl_products")
    .update(updates)
    .eq("id", id)
    .eq("is_deleted", false)
    .select(PRODUCT_SELECT)
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }
  if (!data) {
    throw new Error("Product not found");
  }

  return data as Product;
}

const INVENTORY_TOTAL_FIELDS = [
  "row_count",
  "qty_in_stock",
  "qty_sampled",
  "qty_sold",
  "total_cost",
] as const;

export type InventoryListTotals = Record<(typeof INVENTORY_TOTAL_FIELDS)[number], number>;

/**
 * Row count and the three stock quantities for the whole filtered set.
 *
 * In stock, out on sample and sold stay separate figures. A sampled unit has
 * left the building but has not been bought — it can come back, convert, or
 * lead to a larger order — so folding it into either of the others asserts an
 * outcome nobody knows yet.
 */
function getInventoryTotals(
  params: Pick<InventoryProductsParams, "scope" | "search">,
): Promise<InventoryListTotals> {
  return fetchListTotals(
    "inventory_list_totals",
    {
      p_scope: params.scope,
      p_search: params.search?.trim() || null,
    },
    INVENTORY_TOTAL_FIELDS,
  );
}

export function useInventoryProducts(params: InventoryProductsParams) {
  return useQuery({
    queryKey: productKeys.list({
      limit: params.limit,
      offset: params.offset,
      scope: params.scope,
      search: params.search,
      sort: params.sort ?? null,
    }),
    queryFn: () => getInventoryProducts(params),
  });
}

/**
 * Totals for the whole filtered set.
 *
 * Keyed on scope and search alone: paging or re-sorting cannot move a total.
 */
export function useInventoryTotals(params: Pick<InventoryProductsParams, "scope" | "search">) {
  const scope = params.scope;
  const search = params.search ?? "";

  return useQuery({
    queryKey: productKeys.totals({ scope, search }),
    queryFn: () => getInventoryTotals({ scope, search }),
  });
}

/** Every product the current scope and search select, for CSV export. */
export function fetchInventoryProductsForExport(
  params: Omit<InventoryProductsParams, "limit" | "offset">,
): Promise<ProductWithStock[]> {
  return fetchAllFilteredRows<ProductWithStock>(({ limit, offset }) =>
    getInventoryProducts({ ...params, limit, offset }),
  );
}

export function useInventoryProduct(productId: string | null | undefined) {
  return useQuery({
    enabled: Boolean(productId),
    queryKey: productId ? productKeys.detail(productId) : productKeys.details(),
    queryFn: () => getInventoryProduct(productId as string),
  });
}

/**
 * Soft-deletes one product.
 *
 * The row belongs to a purchase order whose `total_amount`, `balance`, and
 * `payment_status` the database recalculates from its lines, so a write issued
 * from `/inventory` moves the same purchase-order, kanban, and dashboard
 * surfaces a write from the order itself does.
 */
export function useDeleteInventoryProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteInventoryProductRecord(id),
    onSuccess: (product) =>
      reconcileProductWrite(queryClient, product.purchase_order_id ?? undefined),
  });
}

/** Applies an edited product form. Reconciles the owning purchase order with it. */
export function useUpdateInventoryProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: { id: string; payload: UpdateProductPayload }) =>
      updateInventoryProductRecord(params.id, params.payload),
    onSuccess: (product) =>
      reconcileProductWrite(queryClient, product.purchase_order_id ?? undefined),
  });
}

/**
 * A single product field edited from the inventory table.
 *
 * The patch lands on the cached row immediately, but stock is derived from
 * invoice and estimate lines by the `product_stock` view, so a changed
 * quantity moves `qty_in_stock` too and only the server can say by how much.
 * Invalidating the whole product subtree on settle is what reconciles the row
 * and the totals strip with the view.
 *
 * A committed quantity or cost also restates the owning purchase order's
 * totals, whose caches are disjoint roots the single `invalidateKey` cannot
 * reach. Reconciling them from the mutation itself keeps that to a landed
 * write: a rejected edit rolls the row back and leaves the order alone.
 */
export function useInlineEditInventoryProduct() {
  const queryClient = useQueryClient();

  return useInlineEditMutation<ProductWithStock, { id: string; payload: UpdateProductPayload }>({
    getRowId: (product) => product.id,
    invalidateKey: productKeys.all,
    listKey: productKeys.lists(),
    mutationFn: async ({ id, payload }) => {
      const product = await updateInventoryProductRecord(id, payload);
      await reconcileProductWrite(queryClient, product.purchase_order_id ?? undefined);
      return product;
    },
    patch: ({ payload }) => payload,
  });
}
